<script lang="ts">
  import { formatDistance, formatRelativeTime } from '@atlasguess/games';
  import type { GuessRecord } from '../../lib/practiceHistory';

  // Per-city history block shown beneath the round-result distance in
  // City Practice. Two sections:
  //
  //   Summary line — "Visit N · Prior best Xmi · Prior avg Ymi"
  //   Stat table   — three deduped rows: Best ever / Worst ever /
  //                  Last time, each with distance + "3 days ago"
  //
  // The prototype also rendered a rank line ("New personal best!" /
  // "Your Nth-best guess" / "Your worst try so far"), but live play
  // showed it was redundant — the editorial verdict above the panel
  // already gives the moment its character, and the Best/Worst rows
  // make the comparison numerically obvious. Removed.
  //
  // First-visit rendering (no prior guesses) collapses to a one-line
  // "First visit." note — the table needs at least one prior guess to
  // be meaningful.

  interface Props {
    /** All prior guesses at this city, oldest first. */
    pastGuesses: GuessRecord[];
    /** The guess the player just made. Tier-stamped against pastGuesses. */
    currentGuess: GuessRecord;
    /** Display unit, sourced from the user's account setting. */
    units: 'miles' | 'km';
  }

  let { pastGuesses, currentGuess, units }: Props = $props();

  /** Visit count includes the current guess (matches prototype). */
  const visitNumber = $derived(pastGuesses.length + 1);

  const isFirstVisit = $derived(pastGuesses.length === 0);

  /** All guesses including current — used for best/worst computations. */
  const allGuesses = $derived([...pastGuesses, currentGuess]);

  /** Prior best (excludes current) — drives the personal-best check. */
  const priorBestKm = $derived(
    pastGuesses.length === 0
      ? null
      : Math.min(...pastGuesses.map((g) => g.score_km)),
  );

  const priorAvgKm = $derived(
    pastGuesses.length === 0
      ? null
      : pastGuesses.reduce((sum, g) => sum + g.score_km, 0) / pastGuesses.length,
  );

  /**
   * Three rows: Best ever, Worst ever, Last time. Deduped if any of
   * those refer to the same guess (e.g. on visit 2 the current guess
   * might be both "best ever" and "last time" — show once, not twice).
   */
  const tableRows = $derived.by((): Array<{ label: string; guess: GuessRecord }> => {
    if (isFirstVisit) return [];
    const sorted = [...allGuesses].sort((a, b) => a.score_km - b.score_km);
    const bestEver = sorted[0];
    const worstEver = sorted[sorted.length - 1];
    const lastTime = pastGuesses[pastGuesses.length - 1];
    const rows: Array<{ label: string; guess: GuessRecord }> = [];
    const seen = new Set<GuessRecord>();
    for (const [label, guess] of [
      ['Best ever', bestEver],
      ['Worst ever', worstEver],
      ['Last time', lastTime],
    ] as const) {
      if (seen.has(guess)) continue;
      seen.add(guess);
      rows.push({ label, guess });
    }
    return rows;
  });

  function asMs(iso: string): number {
    return new Date(iso).getTime();
  }
</script>

<div class="practice-history">
  {#if isFirstVisit}
    <div class="practice-history__summary">First visit.</div>
  {:else}
    <div class="practice-history__summary">
      Visit {visitNumber}
      {#if priorBestKm !== null}
        <span class="practice-history__sep">·</span>
        Prior best <b>{formatDistance(priorBestKm, units)}</b>
      {/if}
      {#if priorAvgKm !== null}
        <span class="practice-history__sep">·</span>
        Prior avg <b>{formatDistance(priorAvgKm, units)}</b>
      {/if}
    </div>

    <ul class="practice-history__list">
      {#each tableRows as row (row.label)}
        <li class="practice-history__row">
          <span class="practice-history__row-label">{row.label}</span>
          <span class="practice-history__row-distance">
            {formatDistance(row.guess.score_km, units)}
          </span>
          <span class="practice-history__row-when">
            {formatRelativeTime(asMs(row.guess.played_at))}
          </span>
        </li>
      {/each}
    </ul>
  {/if}
</div>
